'use client';

import { UniversalLink } from '@template/components';
import * as S from './styles.ts';
import NumberPickerTabs, {
  INumberPickerTabsProps,
} from '@template/blocks/NumberPickerSection/component/NumberPickerTabs';
import Tables from '@template/blocks/NumberPickerSection/component/Tables';
import {
  NumberPickerFooter,
  NumberPickerFooterLink,
} from './NumberPickerFooter';
import { useTypedDispatch, useTypedSelector } from '@/redux/helpers/hooks.ts';
import { useEffect, useState } from 'react';
import { getInclusiveRemaining } from '@/redux/slices/numbers/thunks/getInclusiveRemaining.ts';
import ArchStandaloneSectionComponent from '@template/blocks/ArchStandaloneSection/component';

interface INumberPickerProps {
  default: INumberPickerTabsProps;
  altStyling?: boolean;
  context?: 'virtualLandline';
}

const numberPickerFooterLinks: NumberPickerFooterLink[] = [
  {
    title: 'London',
    href: '/phone-number/020-london/',
  },
  {
    title: 'Birmingham',
    href: '/phone-number/0121-birmingham/',
  },
  {
    title: 'Leeds',
    href: '/phone-number/0113-leeds/',
  },
  {
    title: 'Glasgow',
    href: '/phone-number/0141-glasgow/',
  },
  {
    title: 'Edinburgh',
    href: '/phone-number/0131-edinburgh/',
  },
  {
    title: 'Liverpool',
    href: '/phone-number/0151-liverpool/',
  },
  {
    title: 'Manchester',
    href: '/phone-number/0161-manchester/',
  },
  {
    title: 'International Numbers',
    href: '/phone-number/international/',
  },
];

const usNumberPickFooterLinks: NumberPickerFooterLink[] = [
  {
    href: '/phone-number/917-new-york',
    title: 'New York',
  },
  {
    href: '/phone-number/213-los-angeles',
    title: 'Los Angeles',
  },
  {
    href: '/phone-number/773-chicago',
    title: 'Chicago',
  },
  {
    href: '/phone-number/832-houston',
    title: 'Houston',
  },
  {
    href: '/phone-number/628-san-francisco',
    title: 'San Francisco',
  },
  {
    href: '/phone-number/206-seattle',
    title: 'Seattle',
  },
  {
    href: '/phone-number/202-washington-dc',
    title: 'Washington',
  },
  {
    href: '/phone-number/international',
    title: 'International Numbers',
  },
];

const NumberPicker = ({
  default: {
    countryCode: defaultCountryCode,
    areaCode: defaultAreaCode,
    type: defaultAreaType,
  },
  altStyling,
  context,
}: INumberPickerProps) => {
  const dispatch = useTypedDispatch();
  const cartItems = useTypedSelector((state) => state.user.cart.items);
  const currency = useTypedSelector(
    (state) => state.store.site_version.currency
  );

  const [searchCountry, setSearchCountry] = useState<string | null>(null);
  const [searchArea, setSearchArea] = useState<string | null>(null);

  const [mounted, setMounted] = useState(false);

  const isUs = currency === 'USD';

  useEffect(() => {
    dispatch(getInclusiveRemaining());
  }, [cartItems.filter((x) => !x.loading).length]);

  useEffect(() => {
    if (mounted) return;

    setMounted(true);

    const searchParams = new URLSearchParams(window.location.search);

    setSearchCountry(searchParams.get('countryCode'));
    setSearchArea(searchParams.get('areaCode'));
  }, [mounted]);

  return (
    <S.NumberPickerSectionWrapper $altStyling={altStyling}>
      {altStyling && (
        <ArchStandaloneSectionComponent
          color='white'
          style={{
            position: 'absolute',
            top: 100,
          }}
        />
      )}
      <S.NumberPickerSectionContainer id='number-search'>
        <NumberPickerTabs
          countryCode={searchCountry || defaultCountryCode}
          areaCode={searchArea || defaultAreaCode}
          type={defaultAreaType}
          defaultKey='buy'
          context={context}
        />
        {context !== 'virtualLandline' && (
          <>
            <Tables />
            <NumberPickerFooter
              links={isUs ? usNumberPickFooterLinks : numberPickerFooterLinks}
              title={
                <>
                  Standard and memorable{' '}
                  <UniversalLink
                    href='/phone-number'
                    title='Link to phone numbers page'
                  >
                    phone numbers
                  </UniversalLink>{' '}
                  are available from Yay.com in all UK STD / area phone number
                  codes and telephone exchanges.{' '}
                  <UniversalLink href='/faq/phone-numbers/0330-numbers/'>
                    Non-geographic
                  </UniversalLink>{' '}
                  and international phone numbers are also available by
                  selecting options from the phone number search.
                </>
              }
            />
          </>
        )}
      </S.NumberPickerSectionContainer>
    </S.NumberPickerSectionWrapper>
  );
};

export default NumberPicker;
